import { isOffline, reportTransportFailure, reportTransportSuccess } from './connectivity'

/**
 * A request that never reached a server: DNS failure, no route, dropped link,
 * TLS failure, timeout.
 *
 * Distinct from `ApiRequestError`, which always carries an HTTP status and
 * therefore proves the server *did* answer. The split matters for messaging —
 * "check your connection" is actively misleading when the real problem is a
 * 500, and "something went wrong" is unhelpful when the user is in a tunnel.
 */
export class NetworkError extends Error {
  /** True when the app believed the device was offline at throw time. */
  readonly offline: boolean

  constructor(message?: string, offline = isOffline()) {
    super(
      message
      ?? (offline
        ? "You're offline. This will work again once you reconnect."
        : "Couldn't reach the server. Check your connection and try again."),
    )
    this.name = 'NetworkError'
    this.offline = offline
  }
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError
}

/**
 * `fetch` with connectivity bookkeeping.
 *
 * Every request doubles as a connectivity sample: a response (any status) is
 * proof the network works, a transport throw is a hint that it doesn't. Caller-
 * initiated aborts are passed through untouched — cancelling your own request
 * says nothing about the network.
 */
export async function netFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    const response = await fetch(input, init)
    reportTransportSuccess()
    return response
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    reportTransportFailure()
    throw new NetworkError()
  }
}
